import React, { useRef, useEffect, useState } from 'react';

export default function FilteredImage({ src, difficulties = [], pixelationStrength = 0.1, className, alt }) {
    const canvasRef = useRef();
    const [revealProgress, setRevealProgress] = useState(0);

    // Animation für progressive Enthüllung
    useEffect(() => {
        let startTimestamp = 0;
        const duration = 10000; // Enthüllung über 10 Sekunden

        function step(timestamp) {
            if (!startTimestamp) startTimestamp = timestamp;
            const elapsed = timestamp - startTimestamp;
            const prog = Math.min(1, elapsed / duration);
            setRevealProgress(prog);
            if (elapsed < duration) {
                requestAnimationFrame(step);
            }
        }

        setRevealProgress(0);
        requestAnimationFrame(step);
    }, [src]);

    useEffect(() => {
        const cacheKey = `cropOffset-${src}`;
        const canvas = canvasRef.current;
        if (!canvas || !src) return;
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = src;

        img.onload = () => {
            const fullW = img.width;
            const fullH = img.height;
            const minRatio = 0.2;
            const currentRatio = minRatio + (1 - minRatio) * revealProgress;
            let cropW = Math.round(fullW * currentRatio);
            let cropH = Math.round(fullH * currentRatio);
            let offsetX = 0;
            let offsetY = 0;

            // Fallback: wenn kein Ausschnitt gefordert, vollständiges Bild nutzen
            if (difficulties.includes('Ausschnitt')) {
                const stored = JSON.parse(localStorage.getItem(cacheKey) || 'null');
                let startX, startY;
                if (stored) {
                    startX = stored.startX;
                    startY = stored.startY;
                } else {
                    const margin = fullW * minRatio / 2;
                    startX = margin + Math.random() * (fullW - 2 * margin);
                    startY = margin + Math.random() * (fullH - 2 * margin);
                    localStorage.setItem(cacheKey, JSON.stringify({ startX, startY }));
                }
                const growthOffset = (1 - currentRatio) * Math.min(fullW, fullH) / 2;
                const x = Math.round(startX - cropW / 2 - growthOffset);
                const y = Math.round(startY - cropH / 2 - growthOffset);
                offsetX = Math.max(0, Math.min(x, fullW - cropW));
                offsetY = Math.max(0, Math.min(y, fullH - cropH));
            } else {
                // Verwende gesamtes Bild
                cropW = fullW;
                cropH = fullH;
                offsetX = 0;
                offsetY = 0;
            }

            // Canvas-Größe anpassen
            canvas.width = cropW;
            canvas.height = cropH;
            ctx.clearRect(0, 0, cropW, cropH);
            ctx.imageSmoothingEnabled = false;

            // Pixelierung
            if (difficulties.includes('Verpixelt')) {
                const off = document.createElement('canvas');
                const pixelRatio = 1 - pixelationStrength;
                off.width = cropW * pixelRatio;
                off.height = cropH * pixelRatio;
                const offCtx = off.getContext('2d');
                offCtx.drawImage(
                    img,
                    offsetX,
                    offsetY,
                    cropW,
                    cropH,
                    0,
                    0,
                    off.width,
                    off.height
                );
                ctx.drawImage(off, 0, 0, off.width, off.height, 0, 0, cropW, cropH);
            } else {
                ctx.drawImage(img, offsetX, offsetY, cropW, cropH, 0, 0, cropW, cropH);
            }

            // Graustufen
            if (difficulties.includes('Schwarz/Weiß')) {
                const imgData = ctx.getImageData(0, 0, cropW, cropH);
                const data = imgData.data;
                for (let i = 0; i < data.length; i += 4) {
                    const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
                    data[i] = avg;
                    data[i + 1] = avg;
                    data[i + 2] = avg;
                }
                ctx.putImageData(imgData, 0, 0);
            }
        };
    }, [src, difficulties, pixelationStrength, revealProgress]);

    return <canvas ref={canvasRef} className={className} aria-label={alt} />;
}
